import { useNavigate, useSearch } from '@tanstack/react-router';
import { Gavel, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import {
  DataTable,
  useDataTable,
  useSearchListState,
  type SearchPatch,
} from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Tabs, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { displayTimeZone, useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { useAuctionColumns } from '../components/auction-columns';
import { AuctionFormDialog } from '../components/auction-form-dialog';
import { AuctionsNav } from '../components/auctions-nav';
import { useAuctions } from '../hooks/use-auctions';
import { auctionTab } from '../schemas/auction-list-search';
import { AUCTION_TABS, type AuctionTab } from '../schemas/auction-rules';

const ROUTE_ID = '/_authed/_active/s/$storeId/auctions/' as const;
const ROUTE_PATH = '/s/$storeId/auctions/' as const;

function Auctions() {
  const { t } = useTranslation('auctions');
  const store = useStore();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: ROUTE_PATH });
  const { editable, reason } = useStoreEditable({ permission: 'auctions.write' });
  const [creating, setCreating] = useState(false);
  const tab = auctionTab(search);

  const onSearchChange = useCallback(
    (patch: SearchPatch) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const list = useSearchListState({ search, onSearchChange });
  const query = useAuctions(store.id, { ...list.params, status: tab });
  const columns = useAuctionColumns(store.id);
  const table = useDataTable({
    mode: 'server',
    data: query.data?.items ?? [],
    nextCursor: query.data?.nextCursor,
    list,
    columns,
    getRowId: (row) => row.id,
    tableId: 'auctions',
  });

  const isEmpty = query.isSuccess && query.data.items.length === 0 && !list.cursor;

  const newButton = (tour?: boolean) =>
    editable ? (
      <Button onClick={() => setCreating(true)} data-tour={tour ? 'auctions.list.new' : undefined}>
        <Plus aria-hidden />
        {t('actions.new')}
      </Button>
    ) : null;

  return (
    <PageContent>
      <PageHeader
        title={t('title')}
        description={t('description', { store: store.name, currency: store.defaultCurrency })}
        actions={newButton(true)}
      />
      {reason === 'closed' ? <ReadOnlyBanner reason="closed" /> : null}
      <AuctionsNav storeId={store.id} />
      <Tabs
        value={tab}
        onValueChange={(value) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              status: value === 'live' ? undefined : (value as AuctionTab),
              cursor: undefined,
            }),
          })
        }
        className="mb-4"
      >
        <TabsList aria-label={t('tabs.label')} data-tour="auctions.list.tabs">
          {AUCTION_TABS.map((value) => (
            <TabsTrigger key={value} value={value}>
              {t(`tabs.${value}`)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {isEmpty ? (
        <EmptyState
          icon={Gavel}
          title={t(`empty.${tab}.title`)}
          description={t(`empty.${tab}.description`)}
          tour="auctions.list.empty"
          tourLink
          action={
            tab === 'live' || tab === 'scheduled' || tab === 'draft' ? newButton() : undefined
          }
        />
      ) : (
        <DataTable
          table={table}
          toolbar={false}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
          onRowClick={(row) =>
            void navigate({
              to: '/s/$storeId/auctions/$auctionId',
              params: { storeId: store.id, auctionId: row.id },
            })
          }
          getRowLabel={(row) => row.lot.title}
          resourceName={{ singular: t('resource.one'), plural: t('resource.other') }}
          emptyState={{ title: t('noMatches') }}
          tour={{ table: 'auctions.list.table' }}
        />
      )}
      {editable ? (
        <AuctionFormDialog
          storeId={store.id}
          currency={store.defaultCurrency}
          timeZone={displayTimeZone(store.timezone)}
          open={creating}
          onOpenChange={setCreating}
          onSaved={(auction) =>
            void navigate({
              to: '/s/$storeId/auctions/$auctionId',
              params: { storeId: store.id, auctionId: auction.id },
            })
          }
        />
      ) : null}
    </PageContent>
  );
}

export function AuctionsPage() {
  return (
    <FeatureErrorBoundary name="auctions.list">
      <Auctions />
    </FeatureErrorBoundary>
  );
}
